import type { CartLine, DeliveryWindow, OrderDraft, SetupDetails } from '../types/catering';

/**
 * The in-progress setup+cart state, the single source of truth for
 * quantities across `/order/*` pages.
 *
 * `setup` is nullable here (unlike `OrderDraft.setup`) because the reducer
 * must represent the state *before* the organizer has picked a delivery
 * window/room/headcount - there is no meaningful placeholder `SetupDetails`
 * to default to. Use `toOrderDraft` to get the finalized, service-ready
 * shape once setup exists.
 */
export type OrderDraftState = {
  readonly setup: SetupDetails | null;
  readonly lines: CartLine[];
};

export type OrderDraftAction =
  | { type: 'setSetup'; setup: SetupDetails }
  | { type: 'setDeliveryWindow'; window: DeliveryWindow }
  | { type: 'setRoom'; room: string }
  | { type: 'setHeadcount'; headcount: number }
  | { type: 'addCartItem'; itemId: number }
  | { type: 'incrementCartItem'; itemId: number }
  | { type: 'decrementCartItem'; itemId: number }
  | { type: 'removeCartItem'; itemId: number }
  | { type: 'resetOrderDraft' };

export function createInitialOrderDraftState(): OrderDraftState {
  return { setup: null, lines: [] };
}

/** Reads the current cart quantity for an item, or 0 when it is not present. */
export function getCartQuantity(state: OrderDraftState, itemId: number): number {
  return state.lines.find((line) => line.itemId === itemId)?.portionsPerPerson ?? 0;
}

/**
 * Produces the finalized `OrderDraft` for `quoteOrder`/`submitOrder`, or
 * `null` when setup has not been established yet. Callers must check for
 * `null` and show the setup step rather than calling the service.
 */
export function toOrderDraft(state: OrderDraftState): OrderDraft | null {
  if (!state.setup) {
    return null;
  }

  return { setup: state.setup, lines: state.lines };
}

/**
 * Adds or updates a cart line so its quantity becomes `nextQuantity(current)`.
 * A quantity that reaches (or would go below) zero removes the line rather
 * than keeping a zero-quantity entry, so `state.lines` only ever holds items
 * actually in the cart.
 */
function withCartQuantity(
  lines: CartLine[],
  itemId: number,
  nextQuantity: (current: number) => number,
): CartLine[] {
  const index = lines.findIndex((line) => line.itemId === itemId);
  const current = index === -1 ? 0 : lines[index].portionsPerPerson;
  const next = Math.max(0, nextQuantity(current));

  if (next === 0) {
    return index === -1 ? lines : lines.filter((line) => line.itemId !== itemId);
  }

  if (index === -1) {
    return [...lines, { itemId, portionsPerPerson: next }];
  }

  return lines.map((line, i) => (i === index ? { itemId, portionsPerPerson: next } : line));
}

/** Non-negative integer headcount, or the previous value if the input is not finite. */
function normalizeHeadcount(headcount: number, previous: number): number {
  if (!Number.isFinite(headcount)) {
    return previous;
  }

  return Math.max(0, Math.trunc(headcount));
}

export function orderDraftReducer(
  state: OrderDraftState,
  action: OrderDraftAction,
): OrderDraftState {
  switch (action.type) {
    case 'setSetup':
      return { ...state, setup: action.setup };

    case 'setDeliveryWindow':
      return state.setup ? { ...state, setup: { ...state.setup, window: action.window } } : state;

    case 'setRoom':
      return state.setup ? { ...state, setup: { ...state.setup, room: action.room } } : state;

    case 'setHeadcount':
      return state.setup
        ? {
            ...state,
            setup: {
              ...state.setup,
              headcount: normalizeHeadcount(action.headcount, state.setup.headcount),
            },
          }
        : state;

    case 'addCartItem':
      return {
        ...state,
        lines: withCartQuantity(state.lines, action.itemId, (current) =>
          current > 0 ? current : 1,
        ),
      };

    case 'incrementCartItem':
      return {
        ...state,
        lines: withCartQuantity(state.lines, action.itemId, (current) => current + 1),
      };

    case 'decrementCartItem':
      return {
        ...state,
        lines: withCartQuantity(state.lines, action.itemId, (current) => current - 1),
      };

    case 'removeCartItem':
      return { ...state, lines: state.lines.filter((line) => line.itemId !== action.itemId) };

    case 'resetOrderDraft':
      return createInitialOrderDraftState();

    default:
      return state;
  }
}
